import { Divider, Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DocumentVersion } from 'src/suitability-report/hooks/useDocumentVersions';
import { DocumentVersionCell } from './DocumentVersionCell';
import { SaveVersionCell } from './SaveVersionCell';
import { useVersionSwapAnimation } from './useVersionSwapAnimation';

type Props = {
  versions: DocumentVersion[];
  selectedVersionId?: string;
  onSaveVersion: () => void;
  onRemoveVersion: (versionId: string) => void;
  onOverwriteVersion: (versionId: string) => void;
  onSelectVersion: (versionId: string) => void;
  onMakePrimary: (versionId: string) => void;
  onDownloadPdf: (fileId: number) => void;
};

const SECONDARY_SLOT_COUNT = 4;

export function VersionPopoverContent({
  versions,
  selectedVersionId,
  onSaveVersion,
  onRemoveVersion,
  onOverwriteVersion,
  onSelectVersion,
  onMakePrimary,
  onDownloadPdf,
}: Props) {
  const { containerRef, capturePositions, animateSwap } = useVersionSwapAnimation();

  const [versionOrder, setVersionOrder] = useState<string[]>(() => getInitialOrder(versions));

  const versionIds = useMemo(
    () =>
      versions
        .map(v => v.id)
        .sort()
        .join(','),
    [versions],
  );

  useEffect(() => {
    setVersionOrder(getInitialOrder(versions));
  }, [versionIds]);

  const orderedVersions = useMemo(() => {
    const versionMap = new Map(versions.map(v => [v.id, v]));
    return versionOrder
      .map(id => versionMap.get(id))
      .filter((v): v is DocumentVersion => v !== undefined);
  }, [versions, versionOrder]);

  const primaryVersion = orderedVersions.find(v => v.isPrimary);
  const secondaryVersions = orderedVersions
    .filter(v => !v.isPrimary)
    .slice(0, SECONDARY_SLOT_COUNT);
  const emptySlots = SECONDARY_SLOT_COUNT - secondaryVersions.length;

  const handleMakePrimary = useCallback(
    (versionId: string) => {
      capturePositions();

      setVersionOrder(prev => {
        const primaryIdx = prev.findIndex(id => versions.find(v => v.id === id)?.isPrimary);
        const targetIdx = prev.findIndex(id => id === versionId);

        const newOrder = [...prev];
        [newOrder[primaryIdx], newOrder[targetIdx]] = [newOrder[targetIdx], newOrder[primaryIdx]];
        return newOrder;
      });

      onMakePrimary(versionId);

      // Wait for React to update the DOM
      requestAnimationFrame(() => {
        // Wait for browser to paint
        requestAnimationFrame(() => {
          animateSwap();
        });
      });
    },
    [capturePositions, animateSwap, onMakePrimary, versions],
  );

  return (
    <Stack gap="sm" ref={containerRef}>
      <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.4">
        Primary version
      </Text>
      {primaryVersion && (
        <DocumentVersionCell
          version={primaryVersion}
          variant="primary"
          isSelected={selectedVersionId === primaryVersion.id}
          onOverwrite={() => onOverwriteVersion(primaryVersion.id)}
          onSelect={() => onSelectVersion(primaryVersion.id)}
          onDownloadPdf={
            primaryVersion.fileId ? () => onDownloadPdf(primaryVersion.fileId!) : undefined
          }
        />
      )}

      <Divider color="gray.1" />

      <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.4">
        Secondary version(s)
      </Text>
      {secondaryVersions.map(version => (
        <DocumentVersionCell
          key={version.id}
          version={version}
          variant="secondary"
          isSelected={selectedVersionId === version.id}
          onRemove={() => onRemoveVersion(version.id)}
          onOverwrite={() => onOverwriteVersion(version.id)}
          onSelect={() => onSelectVersion(version.id)}
          onMakePrimary={() => handleMakePrimary(version.id)}
          onDownloadPdf={version.fileId ? () => onDownloadPdf(version.fileId!) : undefined}
        />
      ))}
      {Array.from({ length: emptySlots }).map((_, i) => (
        <SaveVersionCell key={`empty-${i}`} onSave={onSaveVersion} />
      ))}

      <Text size="xs" fw={FONT_WEIGHTS.bold} c="gray.6" ta="center">
        We store up to 5 versions of your document. All versions, except the primary, will be
        automatically removed once you &apos;Save & finalise document&apos;.
      </Text>
    </Stack>
  );
}

function getInitialOrder(versions: DocumentVersion[]): string[] {
  const primary = versions.find(v => v.isPrimary);
  const secondary = [...versions]
    .filter(v => !v.isPrimary)
    .sort((a, b) => b.versionNumber - a.versionNumber);

  return [...(primary ? [primary.id] : []), ...secondary.map(v => v.id)];
}
